/**
 * Single home for "receive this PZ onto stock" — the DRAFT/CONFIRMED →
 * RECEIVED transition that creates the virtual FIFO batches.
 *
 * Two entry points share it, and must stay identical in what they call, what
 * they say, and how they fail:
 *   - the save-time choice (askWarehouseSaveIntent → POST) on PZ create/edit;
 *   - the "Przyjmij towar wirtualnie" button on an already-saved draft.
 *
 * The transition is idempotent on the backend (inventoryService reprocesses a
 * RECEIVED document safely), so a retry after a partial failure is allowed.
 */

import api from '../../services/api';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';
import { getPZReceiptErrorMessage, isBlockingPZError } from './pzErrorMessages';
import type { FeedbackApi } from '../../utils/feedback';
import type { QuantityUnit } from '../../utils/commercialQuantity';

export interface PZInventoryProcessingSummary {
  batchesCreated: number;
  totalQuantity: number;
  totalQuantityUnit?: QuantityUnit | null;
  totalQuantityPcs?: number | null;
  totalQuantityM2?: number | null;
  totalQuantityM3?: number | null;
  totalCostPLN?: number;
}

export interface ReceivePZDocumentParams {
  pzId: string;
  /** Operator-facing document number, so the toast never reads as a bare "Przyjęto". */
  pzNumber?: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  feedback: FeedbackApi;
  /** True when PUT /pz/:id already persisted the document before receipt. */
  documentSaved?: boolean;
  /**
   * Shared confirmation dialog. WP12 (1.17): a PZ with no purchase invoice is
   * received like a manual PZ "before invoice" after a confirmed warning.
   */
  modal?: Parameters<typeof submitWithOperationalException>[2];
}

export interface ReceivePZDocumentResult {
  ok: boolean;
  inventoryProcessing?: PZInventoryProcessingSummary | null;
}

export async function receivePZDocument(
  params: ReceivePZDocumentParams,
): Promise<ReceivePZDocumentResult> {
  const { pzId, pzNumber, t, feedback, documentSaved = false, modal } = params;

  try {
    const response = modal
      ? await submitWithOperationalException(
        (body) => api.put(`/pz/${pzId}/status`, body),
        { status: 'RECEIVED' },
        modal,
        t as Parameters<typeof submitWithOperationalException>[3],
      )
      : await api.put(`/pz/${pzId}/status`, { status: 'RECEIVED' });
    // A cancelled confirmation received nothing.
    if (response === null) return { ok: false };
    feedback.success(pzNumber
      ? t('pz:success.received', { pzNumber })
      : t('pz:success.receivedFallback'));
    return {
      ok: true,
      inventoryProcessing: (response?.data?.inventoryProcessing as PZInventoryProcessingSummary) ?? null,
    };
  } catch (error) {
    // A blocked receipt is not a retry-and-hope failure: the operator has to
    // fix the document (missing price, cancelled source) before trying again,
    // so the sentence waits for her instead of blinking away.
    feedback.error(getPZReceiptErrorMessage(error, t, 'pz:error.receiveFailed', documentSaved), {
      sticky: isBlockingPZError(error),
    });
    return { ok: false };
  }
}
